import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Shield, Chrome } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if user is already authenticated
    checkAuthStatus();
    
    // Check for session ID in URL fragment
    const fragment = window.location.hash.substring(1);
    const params = new URLSearchParams(fragment);
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      processSessionId(sessionId);
    }
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/check`, {
        withCredentials: true
      });
      
      if (response.data.authenticated) {
        navigate('/admin/dashboard', { replace: true });
      }
    } catch (error) {
      // Not authenticated, continue with login page
      console.log('Not authenticated');
    }
  };

  const processSessionId = async (sessionId) => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/session`, {
        session_id: sessionId
      }, {
        withCredentials: true
      });
      
      if (response.data.success) {
        // Clean URL fragment
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Redirect to admin dashboard
        navigate('/admin/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Session processing error:', error);
      setError('Authentication failed. Please try again.');
      // Clean URL fragment
      window.history.replaceState({}, document.title, window.location.pathname);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = encodeURIComponent(`${window.location.origin}/admin/login`);
    const authUrl = `https://auth.emergentagent.com/?redirect=${redirectUrl}`;
    window.location.href = authUrl;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-vincotek-dark via-gray-800 to-vincotek-dark flex items-center justify-center">
        <Card className="w-full max-w-md mx-4 bg-white/95 backdrop-blur-sm border-0 shadow-2xl">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-vincotek-yellow to-vincotek-blue rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-vincotek-dark mb-4">
              Processing Authentication...
            </h2>
            <p className="text-gray-600">
              Please wait while we verify your credentials.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-vincotek-dark via-gray-800 to-vincotek-dark flex items-center justify-center">
      <div className="absolute inset-0 bg-[url('https://customer-assets.emergentagent.com/job_digital-solutions-40/artifacts/95fmo351_Building%20The%20Future.jpg')] bg-cover bg-center opacity-10"></div>
      
      <Card className="w-full max-w-md mx-4 bg-white/95 backdrop-blur-sm border-0 shadow-2xl relative z-10">
        <CardContent className="p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-vincotek-yellow to-vincotek-blue rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-vincotek-dark mb-2">
              Admin Portal
            </h1>
            <p className="text-gray-600">
              Sign in to manage Vincotek content
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <Button
              onClick={handleGoogleLogin}
              className="w-full bg-vincotek-blue hover:bg-blue-600 text-white h-12 rounded-full font-semibold transition-all duration-300 hover:shadow-lg group"
              disabled={isLoading}
            >
              <Chrome className="mr-3 h-5 w-5" />
              Continue with Google
            </Button>

            <div className="text-center">
              <p className="text-sm text-gray-500">
                Secure authentication
              </p>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-400 mb-4">
              Access is restricted to authorized administrators only.
            </p>
            
            <div className="flex items-center justify-center space-x-2">
              <img 
                src="/assets/Logo%20Vincotek%20Pty%20Ltd-Colour.png" 
                alt="Vincotek Logo" 
                className="h-6 w-auto"
              />
              <span className="text-sm font-semibold text-vincotek-dark">Vincotek</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;